/**
 * Discoverability Journey
 *
 * Single first-14/30-day trigger that picks the next unused feature to surface.
 *
 * Primary responsibilities:
 * - Order Losi-max features by activation day windows
 * - Resolve one next feature when intent signals are weak
 *
 * Dependencies:
 * - Feature registry keys
 */

import type { BetaFeatureKey } from '@/lib/beta-features/registry'
import {
  DISCOVERABILITY_FEATURE_KEYS,
  isDiscoverabilityFeatureAvailable,
  type DiscoverabilityFeatureKey,
  type DiscoverabilityPlanGate
} from './features'
import type { DiscoverabilityUsageSnapshot } from './getDiscoverabilityPrompts'

/** Most users activate in ~14 days; stretch goal is 30. */
export const DISCOVERABILITY_JOURNEY_PRIMARY_DAYS = 14
export const DISCOVERABILITY_JOURNEY_STRETCH_DAYS = 30

/**
 * Ordered Losi-maxing path. Earlier steps unlock first so chat-only users
 * hit Spaces/files before deeper product surfaces.
 */
export const DISCOVERABILITY_JOURNEY_STEPS: Array<{
  featureKey: DiscoverabilityFeatureKey
  /** Inclusive day index (account age) when this step becomes eligible. */
  unlockDay: number
}> = [
  { featureKey: 'spaces', unlockDay: 0 },
  { featureKey: 'files', unlockDay: 0 },
  { featureKey: 'search', unlockDay: 3 },
  { featureKey: 'voice_memos', unlockDay: 3 },
  { featureKey: 'planner', unlockDay: 7 },
  { featureKey: 'images', unlockDay: 7 },
  { featureKey: 'connectors', unlockDay: 10 },
  { featureKey: 'crew', unlockDay: 10 }
]

export type DiscoverabilityJourneyProgress = {
  accountAgeDays: number
  inPrimaryWindow: boolean
  inStretchWindow: boolean
  completedCount: number
  totalCount: number
  nextFeatureKey: DiscoverabilityFeatureKey | null
  unlockedUnusedKeys: DiscoverabilityFeatureKey[]
}

const isUsed = (
  usage: Partial<Record<DiscoverabilityFeatureKey, DiscoverabilityUsageSnapshot>>,
  featureKey: DiscoverabilityFeatureKey
) => {
  const row = usage[featureKey]
  return Boolean(row?.firstUsedAt || (row?.useCount ?? 0) > 0 || row?.completedOnboardingAt)
}

/** `null` when createdAt is missing — do not treat unknown age as day 0 (journey spam). */
export function getAccountAgeDays(
  createdAt: string | Date | null | undefined
): number | null {
  if (!createdAt) return null

  const createdMs = createdAt instanceof Date ? createdAt.getTime() : new Date(createdAt).getTime()
  if (!Number.isFinite(createdMs)) return null

  const ageMs = Date.now() - createdMs
  if (ageMs <= 0) return 0

  return Math.floor(ageMs / (24 * 60 * 60 * 1000))
}

export function getDiscoverabilityJourneyProgress(input: {
  accountAgeDays: number
  usage: Partial<Record<DiscoverabilityFeatureKey, DiscoverabilityUsageSnapshot>>
  userPlan?: DiscoverabilityPlanGate | string | null
  enabledBetaFeatures?: Partial<Record<BetaFeatureKey, boolean>> | null
}): DiscoverabilityJourneyProgress {
  const accountAgeDays = Math.max(0, Math.floor(input.accountAgeDays))
  const isAvailable = (key: DiscoverabilityFeatureKey) =>
    isDiscoverabilityFeatureAvailable(key, {
      userPlan: input.userPlan,
      enabledBetaFeatures: input.enabledBetaFeatures
    })

  const journeyKeys = DISCOVERABILITY_JOURNEY_STEPS.map((step) => step.featureKey)
  const registeredJourneyKeys = journeyKeys.filter(
    (key) => DISCOVERABILITY_FEATURE_KEYS.includes(key) && isAvailable(key)
  )

  // Denominator = currently unlockable (by day + plan/beta), not day-locked future steps.
  const unlockedAvailableKeys = DISCOVERABILITY_JOURNEY_STEPS.filter((step) => {
    if (!DISCOVERABILITY_FEATURE_KEYS.includes(step.featureKey)) return false
    if (!isAvailable(step.featureKey)) return false
    return accountAgeDays >= step.unlockDay
  }).map((step) => step.featureKey)

  const unlockedUnusedKeys = unlockedAvailableKeys.filter(
    (key) => !isUsed(input.usage, key)
  )

  const completedCount = unlockedAvailableKeys.filter((key) => isUsed(input.usage, key)).length

  return {
    accountAgeDays,
    inPrimaryWindow: accountAgeDays <= DISCOVERABILITY_JOURNEY_PRIMARY_DAYS,
    inStretchWindow: accountAgeDays <= DISCOVERABILITY_JOURNEY_STRETCH_DAYS,
    completedCount,
    totalCount: unlockedAvailableKeys.length,
    nextFeatureKey: unlockedUnusedKeys[0] ?? null,
    unlockedUnusedKeys
  }
}
